import { sendChatFeedback, type UiAction, useStreamChatMutation } from '@massilia/queries'
import { cn } from '@massilia/ui'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { ArrowUp, Sparkles, ThumbsDown, ThumbsUp, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import {
  buildFilterSearchPatch,
  CARRIED_OVER_FILTER_KEYS,
  nlpFiltersToUrlParams,
  type SidebarFilterParams,
} from '@/components/flights/filters'
import { type ChatMessage, useChatWidgetStore } from '@/stores/chat-widget.store'

import { FlightDraftDebugPanel } from './flight-draft-debug-panel'

/**
 * Ce que le widget attend de la page qui l'héberge : le routeur dans l'app,
 * une sortie vers l'app sur le WordPress, où il n'y a pas de routeur.
 */
export interface ChatWidgetHost {
  pathname: string
  /** Absent hors de l'app : le serveur n'émet `apply_filters` que depuis la page de résultats. */
  applyFilters?: (nextParams: SidebarFilterParams, replace: boolean) => void
  /** Préfixe des images : vide dans l'app, origine de l'app sur le WordPress. */
  assetOrigin: string
  openFlightResults: (search: SidebarFilterParams & { search: unknown }) => void
  search: SidebarFilterParams
}

interface ChatWidgetProps {
  className?: string
}

/** Le widget de l'app, branché sur le routeur. */
export function ChatWidget({ className }: ChatWidgetProps) {
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const search = useRouterState({ select: (state) => state.location.search })

  const host = useMemo<ChatWidgetHost>(
    () => ({
      pathname,
      applyFilters: (nextParams, replace) =>
        void navigate({
          replace: true,
          // `to` n'est connu qu'à l'exécution : le réducteur n'est donc pas typé par
          // la route. Seuls les filtres nous intéressent, le reste du search est
          // réétalé tel quel par `buildFilterSearchPatch`.
          search: (prev: SidebarFilterParams) => buildFilterSearchPatch(prev, nextParams, replace),
          to: pathname,
        }),
      assetOrigin: '',
      openFlightResults: (nextSearch) =>
        void navigate({ search: nextSearch, to: '/vols-multi/resultats' }),
      search,
    }),
    [navigate, pathname, search]
  )

  return <ChatWidgetPanel className={className} host={host} />
}

const QUICK_MESSAGES = [
  {
    behavior: 'send' as const,
    image: '/images/chat/flight.jpg',
    isGold: true,
    label: 'Rechercher un vol au meilleur prix',
    subtitle: 'IA + tarifs agence',
    value: 'yes_flight_search',
  },
  {
    behavior: 'send' as const,
    image: '/images/chat/roadtrip.jpg',
    isGold: false,
    label: 'Organiser mon voyage',
    text: 'Comment organiser mon voyage avec Massilia Voyages ?',
  },
  {
    behavior: 'send' as const,
    image: '/images/chat/destination.jpg',
    isGold: false,
    label: 'Guides destinations',
    text: 'Quels sont vos guides de destinations disponibles ? Je cherche des conseils voyage.',
  },
  {
    behavior: 'focus' as const,
    image: '/images/chat/beach.jpg',
    isGold: false,
    label: 'Poser une question',
    text: 'Un problème particulier ? Posez la question directement',
  },
] as const

interface UiActionRendererProps {
  action: UiAction
  isSending: boolean
  onButtonClick: (value: string, label?: string) => void
}

// ---------------------------------------------------------------------------
// Main widget
// ---------------------------------------------------------------------------

export function ChatWidgetPanel({ className, host }: ChatWidgetProps & { host: ChatWidgetHost }) {
  const streamChatMutation = useStreamChatMutation()
  const chatMode = useChatWidgetStore((state) => state.chatMode)
  const error = useChatWidgetStore((state) => state.error)
  const input = useChatWidgetStore((state) => state.input)
  const isOpen = useChatWidgetStore((state) => state.isOpen)
  const isWaitingForFirstChunk = useChatWidgetStore((state) => state.isWaitingForFirstChunk)
  const isSending = useChatWidgetStore((state) => state.isSending)
  const messages = useChatWidgetStore((state) => state.messages)
  const sendMessage = useChatWidgetStore((state) => state.sendMessage)
  const canOpenFlightResults = useChatWidgetStore((state) => state.canOpenFlightResults)
  const closeChat = useChatWidgetStore((state) => state.closeChat)
  const consumeNavigationUiActions = useChatWidgetStore((state) => state.consumeNavigationUiActions)
  const handleUiButtonClick = useChatWidgetStore((state) => state.handleUiButtonClick)
  const setFeedback = useChatWidgetStore((state) => state.setFeedback)
  const setInput = useChatWidgetStore((state) => state.setInput)
  const resetConversation = useChatWidgetStore((state) => state.resetConversation)
  const setShowQuickActions = useChatWidgetStore((state) => state.setShowQuickActions)
  const showQuickActions = useChatWidgetStore((state) => state.showQuickActions)
  const toggleOpen = useChatWidgetStore((state) => state.toggleOpen)
  const waitingStageLabel = useChatWidgetStore((state) => state.waitingStageLabel)

  const prevIsOpenRef = useRef(isOpen)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    const container = scrollContainerRef.current
    if (container) {
      container.scrollTo({ behavior: 'smooth', top: container.scrollHeight })
    }
  }, [messages.length])

  useEffect(() => {
    if (!isOpen) return
    requestAnimationFrame(() => {
      const container = scrollContainerRef.current
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    })
  }, [isOpen, isSending, showQuickActions])

  useEffect(() => {
    const didReopen = isOpen && !prevIsOpenRef.current
    if (didReopen && !isSending && messages.length === 0) {
      setShowQuickActions(true)
    }
    prevIsOpenRef.current = isOpen
  }, [isOpen, isSending, messages.length, setShowQuickActions])

  // Carry over any sidebar filters from the current results page so a follow-up
  // search initiated from the chat keeps the user's chosen filters.
  const currentLocationSearch = host.search
  const currentLocationPath = host.pathname
  const sidebarFilterCarryover = useMemo<SidebarFilterParams>(() => {
    const src = currentLocationSearch
    const carried = CARRIED_OVER_FILTER_KEYS.filter((key) => src[key] !== undefined).map(
      (key) => [key, src[key]] as const
    )
    // `Object.fromEntries` perd le lien clé/valeur : la forme est celle des clés
    // reprises juste au-dessus, toutes optionnelles dans le contrat de la route.
    return Object.fromEntries(carried)
  }, [currentLocationSearch])

  // Page context is OWNED by the flight-results route (it has priceRange,
  // availableAirlines, currentFilters all in one place). The widget only
  // clears it when the user leaves that route — never sets it, to avoid
  // overwriting the enriched value from the route.
  const setPageContext = useChatWidgetStore((state) => state.setPageContext)
  useEffect(() => {
    if (!currentLocationPath.startsWith('/vols-multi/resultats')) {
      setPageContext(undefined)
    }
  }, [currentLocationPath, setPageContext])

  // Auto-navigate to flight results when open_flight_results action appears,
  // and apply filters in place when apply_filters action appears.
  // After running, the navigation actions are consumed from the message so
  // they cannot replay on reload, debounce expiry, or unrelated URL changes
  // (which would otherwise hijack a fresh navigation or re-open the widget).
  useEffect(() => {
    if (isSending) return
    const lastIndex = messages.length - 1
    const lastMsg = messages[lastIndex]
    if (lastMsg?.role !== 'assistant' || !lastMsg.uiActions) return

    const applyAction = lastMsg.uiActions.find((a) => a.type === 'apply_filters')
    if (applyAction && applyAction.type === 'apply_filters' && host.applyFilters) {
      host.applyFilters(nlpFiltersToUrlParams(applyAction.filters), applyAction.mode === 'replace')
      consumeNavigationUiActions(lastIndex)
      // Don't close the chat — the user wants to see the filters refresh below.
      return
    }

    const structuredAction = lastMsg.uiActions.find(
      (a) => a.type === 'open_flight_results_structured'
    )
    if (structuredAction && structuredAction.type === 'open_flight_results_structured') {
      const key = JSON.stringify({
        filters: structuredAction.filters,
        search: structuredAction.searchParams,
      })
      if (!canOpenFlightResults(key)) return
      const extracted = structuredAction.filters
        ? nlpFiltersToUrlParams(structuredAction.filters)
        : {}
      host.openFlightResults({
        ...sidebarFilterCarryover,
        ...extracted,
        search: structuredAction.searchParams,
      })
      consumeNavigationUiActions(lastIndex)
      closeChat()
      return
    }
  }, [
    messages,
    isSending,
    canOpenFlightResults,
    closeChat,
    consumeNavigationUiActions,
    host,
    sidebarFilterCarryover,
  ])

  const SCROLL_THRESHOLD_PX = 100
  const isNearBottom = () => {
    const el = scrollContainerRef.current
    if (!el) return true
    return el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD_PX
  }

  const onAssistantChunkRendered = () => {
    requestAnimationFrame(() => {
      // Scroll the container instead of using scrollIntoView which steals focus from textarea
      const container = scrollContainerRef.current
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    })
  }

  const sendCurrentInput = async () => {
    await sendMessage({
      isNearBottom,
      onAssistantChunkRendered,
      streamAssistantReply: streamChatMutation.mutateAsync,
    })
  }

  const handleQuickMessage = async (msg: (typeof QUICK_MESSAGES)[number]) => {
    if (isSending) return
    setShowQuickActions(false)
    if (msg.behavior === 'focus') {
      // Add a chatbot message inviting the user to type, then focus the input
      useChatWidgetStore.setState((current) => ({
        messages: [
          ...current.messages,
          {
            content:
              'Comment puis-je vous aider ? 😊 Décrivez votre question ou problème ci-dessous !',
            role: 'assistant' as const,
          },
        ],
      }))
      inputRef.current?.focus()
      return
    }
    const serverValue = 'value' in msg ? msg.value : msg.text
    const displayText = msg.label
    setInput(serverValue)
    await handleUiButtonClick({
      displayLabel: displayText,
      isNearBottom,
      onAssistantChunkRendered,
      streamAssistantReply: streamChatMutation.mutateAsync,
      value: serverValue,
    })
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (input.trim()) {
      setShowQuickActions(false)
    }
    await sendCurrentInput()
  }

  const handleUiButtonValue = async (value: string, label?: string) => {
    if (isSending) return

    setShowQuickActions(false)
    await handleUiButtonClick({
      displayLabel: label,
      isNearBottom,
      onAssistantChunkRendered,
      streamAssistantReply: streamChatMutation.mutateAsync,
      value,
    })
  }

  const shouldShowQuickActions = showQuickActions && !isSending

  const loadingLabel = waitingStageLabel ?? 'Je réfléchis...'

  return (
    <>
      {/* ----------------------------------------------------------------- */}
      {/* Chat panel                                                        */}
      {/* ----------------------------------------------------------------- */}
      {isOpen ? (
        <div
          className={cn(
            'fixed z-50 flex flex-col overflow-hidden',
            // Mobile: full screen with small margins
            'inset-2 bottom-2 sm:inset-auto',
            // Desktop: positioned bottom-right with fixed size
            'sm:right-4 sm:bottom-24 sm:h-[78vh] sm:max-h-[860px] sm:w-[420px]',
            'rounded-2xl',
            // Opaque : sur le WordPress, le panneau passe au-dessus de photos pleine page.
            'bg-white',
            'border border-white/50',
            'shadow-2xl shadow-sky-900/10',
            'ring-1 ring-sky-200/20',
            'animate-in fade-in slide-in-from-bottom-4 duration-300',
            className
          )}
        >
          {/* ------ Header ------ */}
          <div className="relative overflow-hidden px-4 py-3">
            {/* Gradient layers with opacity crossfade */}
            <div
              className={cn(
                'pointer-events-none absolute inset-0 bg-gradient-to-r from-sky-500 via-sky-400 to-cyan-500 transition-opacity duration-500',
                chatMode === 'flight_request' ? 'opacity-0' : 'opacity-100'
              )}
            />
            <div
              className={cn(
                'pointer-events-none absolute inset-0 bg-gradient-to-r from-amber-500 via-orange-400 to-amber-500 transition-opacity duration-500',
                chatMode === 'flight_request' ? 'opacity-100' : 'opacity-0'
              )}
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div
                    className={cn(
                      'flex h-9 w-9 items-center justify-center rounded-full shadow-inner shadow-white/10 backdrop-blur-sm transition-colors duration-500',
                      chatMode === 'flight_request' ? 'bg-white/25' : 'bg-white/20'
                    )}
                  >
                    {chatMode === 'flight_request' ? (
                      <span className="text-base">✈️</span>
                    ) : (
                      <Sparkles className="h-4 w-4 text-white" />
                    )}
                  </div>
                  <span
                    className={cn(
                      'absolute right-0 bottom-0 h-2.5 w-2.5 rounded-full shadow-sm ring-2 transition-colors duration-500',
                      chatMode === 'flight_request'
                        ? 'bg-emerald-400 shadow-emerald-400/50 ring-amber-500'
                        : 'bg-emerald-400 shadow-emerald-400/50 ring-sky-500'
                    )}
                  />
                </div>
                <div>
                  <span className="text-sm font-semibold text-white drop-shadow-sm">Helix</span>
                  <span className="block text-xs text-white/70">
                    {chatMode === 'flight_request' ? 'Recherche de vol' : 'Assistant voyage'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  className="rounded-full bg-white/20 px-3 py-1.5 text-xs font-semibold text-white shadow-sm ring-1 ring-white/30 transition hover:bg-white/30 hover:text-white"
                  onClick={() => {
                    if (messages.length === 0) return
                    resetConversation()
                  }}
                  type="button"
                >
                  Effacer
                </button>
                <button
                  aria-label="Fermer le chat"
                  className="-my-1 inline-flex size-11 items-center justify-center rounded-full text-white/60 transition hover:bg-white/15 hover:text-white sm:my-0 sm:size-auto sm:p-1.5"
                  onClick={toggleOpen}
                  type="button"
                >
                  <X className="h-5 w-5 sm:h-4 sm:w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* ------ Messages ------ */}
          <div
            ref={scrollContainerRef}
            className={cn(
              'flex-1 space-y-3 overflow-y-auto px-3 py-3 text-[13px]',
              'bg-gradient-to-b from-sky-50/40 via-white/30 to-white/50',
              '[&::-webkit-scrollbar]:w-1.5',
              '[&::-webkit-scrollbar-track]:bg-transparent',
              '[&::-webkit-scrollbar-thumb]:rounded-full',
              '[&::-webkit-scrollbar-thumb]:bg-sky-200/50',
              '[&::-webkit-scrollbar-thumb:hover]:bg-sky-300/60'
            )}
          >
            {messages.length > 0
              ? messages.map((message, index) => (
                  <div key={`${message.role}-${index}-${message.content.slice(0, 16)}`}>
                    <div
                      className={cn(
                        'flex',
                        message.role === 'user' ? 'justify-end' : 'justify-start'
                      )}
                    >
                      {message.role === 'assistant' ? (
                        <div className="mt-1 mr-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-400 to-cyan-500 shadow-sm shadow-sky-400/20">
                          <Sparkles className="h-3 w-3 text-white" />
                        </div>
                      ) : null}

                      <div
                        className={cn(
                          'max-w-[78%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed',
                          message.role === 'user'
                            ? 'rounded-br-md bg-gradient-to-br from-sky-500 via-sky-500 to-cyan-500 text-white shadow-md shadow-sky-500/20'
                            : 'rounded-bl-md border border-sky-100/40 bg-gradient-to-br from-white/80 to-sky-50/40 text-slate-700 shadow-sm backdrop-blur-md'
                        )}
                      >
                        <ChatMessageContent
                          isLastMessage={index === messages.length - 1}
                          isWaitingForFirstChunk={isWaitingForFirstChunk}
                          loadingLabel={loadingLabel}
                          message={message}
                        />
                      </div>
                    </div>

                    {message.role === 'assistant' &&
                      message.uiActions &&
                      message.uiActions.length > 0 &&
                      index === messages.length - 1 && (
                        <div className="mt-2 ml-8 flex flex-col gap-2">
                          {message.uiActions.map((action, actionIndex) => (
                            <UiActionRenderer
                              key={actionIndex}
                              action={action}
                              isSending={isSending}
                              onButtonClick={(value, label) => {
                                void handleUiButtonValue(value, label)
                              }}
                            />
                          ))}
                        </div>
                      )}

                    {/* Feedback buttons for assistant messages */}
                    {message.role === 'assistant' && message.content && !isSending && (
                      <FeedbackButtons
                        feedback={message.feedbackGiven}
                        onFeedback={(value) => {
                          setFeedback(index, value)
                          if (message.traceId) {
                            void sendChatFeedback(message.traceId, value === 'up' ? 1 : -1)
                          }
                        }}
                      />
                    )}
                  </div>
                ))
              : null}

            {/* ------ Quick actions ------ */}
            {shouldShowQuickActions ? (
              <div className={cn('flex flex-col gap-3', messages.length > 0 ? 'mt-4' : 'mt-1')}>
                {messages.length > 0 ? (
                  <div className="mt-2 mb-1 flex items-center gap-2">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-sky-300/40 to-transparent" />
                    <span className="text-[10px] font-medium tracking-wide text-sky-400/80 uppercase">
                      Suggestions
                    </span>
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-sky-300/40 to-transparent" />
                  </div>
                ) : null}

                {messages.length === 0 ? (
                  <div className="rounded-2xl border border-sky-200/30 bg-gradient-to-br from-white/70 to-sky-50/40 px-5 py-5 shadow-sm backdrop-blur-sm">
                    <p className="mb-2 bg-gradient-to-r from-sky-600 to-cyan-600 bg-clip-text text-lg font-extrabold tracking-tight text-transparent">
                      Bienvenue chez Massilia&nbsp;Voyages
                    </p>
                    <p className="text-sm leading-relaxed font-medium text-slate-500">
                      Je suis <span className="font-semibold text-sky-600">Helix</span>, votre
                      assistant voyage. Comment puis-je vous aider ?
                    </p>
                  </div>
                ) : null}

                {/* Gold flight CTA — full width image card */}
                <button
                  className={cn(
                    'group relative w-full overflow-hidden rounded-xl',
                    'shadow-md shadow-amber-400/15 transition-all duration-300',
                    'hover:shadow-lg hover:shadow-amber-400/25',
                    'focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-1 focus-visible:outline-none',
                    'active:scale-[0.98]'
                  )}
                  disabled={isSending}
                  onClick={() => void handleQuickMessage(QUICK_MESSAGES[0])}
                  type="button"
                >
                  <img
                    alt=""
                    className="h-24 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    src={host.assetOrigin + QUICK_MESSAGES[0].image}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-amber-900/80 via-amber-900/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 px-4 py-3">
                    <p className="text-sm font-bold text-white drop-shadow-md">
                      ✈️ {QUICK_MESSAGES[0].label}
                    </p>
                    <p className="text-[11px] text-amber-100/90">{QUICK_MESSAGES[0].subtitle}</p>
                  </div>
                </button>

                {/* 3 service cards — symmetric grid */}
                <div className="grid grid-cols-3 gap-2">
                  {QUICK_MESSAGES.slice(1).map((msg) => (
                    <button
                      key={msg.label}
                      className={cn(
                        'group relative overflow-hidden rounded-xl',
                        'shadow-sm transition-all duration-300',
                        'hover:shadow-md',
                        'focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-1 focus-visible:outline-none',
                        'active:scale-[0.97]'
                      )}
                      disabled={isSending}
                      onClick={() => void handleQuickMessage(msg)}
                      type="button"
                    >
                      <img
                        alt=""
                        className="h-20 w-full object-cover transition-transform duration-500 group-hover:scale-110"
                        src={host.assetOrigin + msg.image}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/75 via-slate-900/20 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 px-2 py-2">
                        <p className="text-[11px] leading-tight font-semibold text-white drop-shadow-md">
                          {msg.label}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {error ? (
              <p className="mt-2 rounded-xl border border-red-200/40 bg-gradient-to-r from-red-50/80 to-red-50/50 px-3 py-2 text-xs text-red-600 backdrop-blur-sm">
                {error}
                {error && !error.endsWith('.') ? '.' : null} Veuillez reessayer.
              </p>
            ) : null}
          </div>

          <FlightDraftDebugPanel />

          {/* ------ Input area ------ */}
          <form
            className="border-t border-sky-100/20 bg-gradient-to-t from-white/70 to-white/50 px-3 py-2.5 backdrop-blur-xl"
            onSubmit={(event) => void handleSubmit(event)}
          >
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                aria-label="Votre message"
                className={cn(
                  'max-h-28 min-h-[2.25rem] flex-1 resize-none rounded-xl px-3.5 py-2 text-[13px] text-slate-800',
                  'bg-white/60 border border-sky-200/30 backdrop-blur-sm',
                  'shadow-sm shadow-sky-100/20 ring-0 outline-none',
                  'placeholder:text-slate-400',
                  'focus:border-sky-400/50 focus:bg-white/80 focus:ring-2 focus:ring-sky-400/15 focus:shadow-md focus:shadow-sky-200/20',
                  'transition-all duration-200'
                )}
                onChange={(event) => {
                  setInput(event.target.value)
                  const el = event.target
                  el.style.height = 'auto'
                  el.style.height = `${Math.min(el.scrollHeight, 112)}px`
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    if (isSending) return
                    void handleSubmit(event)
                  }
                }}
                placeholder={
                  chatMode === 'flight_request'
                    ? 'Decrivez votre vol...'
                    : 'Ecrivez votre message...'
                }
                rows={1}
                value={input}
              />
              <button
                aria-label="Envoyer le message"
                className={cn(
                  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
                  'bg-gradient-to-br from-sky-500 via-sky-500 to-cyan-500 text-white',
                  'shadow-md shadow-sky-500/25',
                  'transition-all duration-200',
                  isSending || !input.trim()
                    ? 'cursor-not-allowed opacity-30 shadow-none'
                    : 'hover:shadow-lg hover:shadow-sky-500/35 hover:brightness-110 active:scale-90'
                )}
                disabled={isSending || !input.trim()}
                type="submit"
              >
                <ArrowUp className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {/* ----------------------------------------------------------------- */}
      {/* Floating action button                                            */}
      {/* ----------------------------------------------------------------- */}
      <button
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Fermer le widget de chat' : 'Ouvrir le widget de chat'}
        className={cn(
          'fixed right-6 bottom-6 z-40 flex h-14 w-14 items-center justify-center rounded-full',
          'bg-gradient-to-br from-sky-400 via-sky-500 to-cyan-600 text-white',
          'shadow-lg shadow-sky-500/30',
          'ring-1 ring-white/20',
          'transition-all duration-300',
          'hover:scale-110 hover:shadow-xl hover:shadow-sky-500/40',
          'active:scale-95',
          className
        )}
        onClick={toggleOpen}
        type="button"
      >
        {isOpen ? (
          <X className="h-6 w-6 drop-shadow-sm" />
        ) : (
          <Sparkles className="h-6 w-6 drop-shadow-sm" />
        )}
      </button>
    </>
  )
}

// ---------------------------------------------------------------------------
// Markdown renderer
// ---------------------------------------------------------------------------

function ChatMarkdown({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ children, ...props }) => (
          <a
            {...props}
            className="text-sky-500 underline decoration-sky-300/50 underline-offset-2 transition hover:text-sky-600 hover:decoration-sky-400/70"
            rel="noreferrer"
            target="_blank"
          >
            {children}
          </a>
        ),
        code: ({ className, children, ...props }) => {
          const match = /language-(\w+)/.exec(className ?? '')
          const isBlock = Boolean(match)
          return isBlock ? (
            <code
              {...props}
              className="block rounded-lg bg-gradient-to-br from-slate-800 to-slate-900 px-3 py-2 font-mono text-[12px] leading-relaxed whitespace-pre-wrap text-slate-100 shadow-inner"
            >
              {children}
            </code>
          ) : (
            <code
              {...props}
              className="rounded-md bg-sky-100/40 px-1.5 py-0.5 font-mono text-[12px] text-sky-700"
            >
              {children}
            </code>
          )
        },
        ol: ({ children, ...props }) => (
          <ol {...props} className="list-decimal space-y-1 pl-5">
            {children}
          </ol>
        ),
        p: ({ children, ...props }) => (
          <p {...props} className="whitespace-pre-wrap">
            {children}
          </p>
        ),
        ul: ({ children, ...props }) => (
          <ul {...props} className="list-disc space-y-1 pl-5">
            {children}
          </ul>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  )
}

/** Contenu d'une bulle de message : dots de chargement, markdown assistant en
 * cours de génération, markdown assistant terminé, ou texte brut utilisateur. */
function ChatMessageContent({
  isLastMessage,
  isWaitingForFirstChunk,
  loadingLabel,
  message,
}: {
  isLastMessage: boolean
  isWaitingForFirstChunk: boolean
  loadingLabel: string
  message: ChatMessage
}) {
  if (message.role === 'assistant' && message.content === '' && isWaitingForFirstChunk) {
    return (
      <div className="flex items-center gap-2.5">
        <div className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-sky-400 [animation-delay:-0.3s]" />
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-sky-400 [animation-delay:-0.15s]" />
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-sky-400" />
        </div>
        <span className="text-xs text-slate-400">{loadingLabel}</span>
      </div>
    )
  }

  if (message.role === 'assistant') {
    return (
      <div className="space-y-2 break-words">
        <ChatMarkdown content={message.content} />
        {/* Show loading dots after existing content when generating confirmation */}
        {isWaitingForFirstChunk && isLastMessage && (
          <div className="mt-2 flex items-center gap-1">
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-sky-400 [animation-delay:-0.3s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-sky-400 [animation-delay:-0.15s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-sky-400" />
          </div>
        )}
      </div>
    )
  }

  return <p className="break-words whitespace-pre-wrap">{message.content}</p>
}

// ---------------------------------------------------------------------------
// UiAction renderer
// ---------------------------------------------------------------------------

function FeedbackButtons({
  feedback,
  onFeedback,
}: {
  feedback?: 'down' | 'up'
  onFeedback: (value: 'down' | 'up') => void
}) {
  return (
    <div className="mt-1 ml-8 flex items-center gap-1">
      <button
        aria-label="Bonne réponse"
        className={cn(
          'rounded-full p-1 transition',
          feedback === 'up' ? 'text-emerald-500' : 'text-stone-300 hover:text-emerald-400'
        )}
        disabled={!!feedback}
        onClick={() => onFeedback('up')}
        type="button"
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </button>
      <button
        aria-label="Mauvaise réponse"
        className={cn(
          'rounded-full p-1 transition',
          feedback === 'down' ? 'text-red-400' : 'text-stone-300 hover:text-red-400'
        )}
        disabled={!!feedback}
        onClick={() => onFeedback('down')}
        type="button"
      >
        <ThumbsDown className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

// Inline "Autre ? précisez" pill rendered next to quick-reply buttons.
// Submitting it (Enter or the send arrow) is equivalent to typing the same
// text in the main chat textarea.
function FreeInputPill({
  isSending,
  onSubmit,
  placeholder,
}: {
  isSending: boolean
  onSubmit: (text: string) => void
  placeholder: string
}) {
  const [value, setValue] = useState('')

  const commit = () => {
    const trimmed = value.trim()
    if (!trimmed || isSending) return
    onSubmit(trimmed)
    setValue('')
  }

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 rounded-xl border border-sky-200/40 bg-white/70 py-1 pr-1 pl-3 text-[11px] shadow-sm backdrop-blur-sm transition focus-within:border-sky-400 focus-within:ring-2 focus-within:ring-sky-400/40',
        isSending && 'cursor-not-allowed opacity-40'
      )}
    >
      <input
        className="w-32 bg-transparent text-[11px] font-medium text-sky-900 placeholder:text-slate-400 focus:outline-none"
        disabled={isSending}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          }
        }}
        placeholder={placeholder}
        type="text"
        value={value}
      />
      <button
        aria-label="Envoyer"
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br from-sky-500 to-cyan-500 text-white shadow-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40'
        )}
        disabled={isSending || !value.trim()}
        onClick={commit}
        type="button"
      >
        <ArrowUp className="h-3 w-3" />
      </button>
    </div>
  )
}

function UiActionRenderer({ action, isSending, onButtonClick }: UiActionRendererProps) {
  if (action.type === 'buttons') {
    return (
      <div className="flex flex-col gap-1.5">
        {action.prompt && <p className="text-[11px] font-medium text-slate-400">{action.prompt}</p>}
        <div className="flex flex-wrap gap-2">
          {action.options.map((opt, i) => (
            <button
              key={opt.id}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-1.5 text-[11px] font-medium transition-all duration-200',
                'focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none',
                'active:scale-[0.96]',
                i === 0
                  ? 'bg-gradient-to-r from-sky-500 via-sky-500 to-cyan-500 text-white shadow-md shadow-sky-500/20 hover:shadow-lg hover:shadow-sky-500/30 hover:brightness-110 focus-visible:ring-sky-400'
                  : 'border border-sky-200/30 bg-gradient-to-br from-white/60 to-sky-50/30 text-sky-700 shadow-sm backdrop-blur-sm hover:from-white/80 hover:to-sky-100/40 hover:border-sky-300/40 hover:shadow-md focus-visible:ring-sky-400',
                isSending && 'cursor-not-allowed opacity-40'
              )}
              disabled={isSending}
              onClick={() => onButtonClick(opt.value, opt.label)}
              type="button"
            >
              {opt.label}
            </button>
          ))}
          {action.freeInput && (
            <FreeInputPill
              isSending={isSending}
              onSubmit={(text) => onButtonClick(text, text)}
              placeholder={action.freeInput.placeholder ?? 'Autre ? précisez'}
            />
          )}
        </div>
      </div>
    )
  }

  return null
}
